"use client";

/**
 * AssistantSection — pinned vertical→horizontal scroll walkthrough of the AI Assistant.
 *
 * Mechanic: a tall `.spacer` (N * 132vh) wraps a `position: sticky` `.pin` that holds
 * a horizontal `.h-track` of N full-width `.panel`s. As the page scrolls through the
 * spacer, we map the spacer's scroll progress (0→1) to a panel position (0→N-1) through
 * `holdMap()` — an easing that lingers on each panel before sliding to the next — and
 * translate the `.h-track` horizontally. The same position drives the progress rail
 * (`.track-fill` + `.node`s) and the active-panel counter.
 *
 * Updates are driven from scroll/resize events (rAF-coalesced) rather than a permanent
 * rAF loop, to save battery. AssistantChatDemo owns the per-panel chat animation; this
 * component only tells it which panel is `active`.
 */

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useTranslation } from "@/lib/i18n/context";
import { AssistantChatDemo } from "./AssistantChatDemo";

function holdMap(rawPos: number, N: number) {
  const k = Math.floor(rawPos);
  if (k >= N - 1) return N - 1;
  const f = rawPos - k;
  const holdStart = k === 0 ? 0.58 : 0.3;
  let t = (f - holdStart) / 0.4;
  t = Math.min(1, Math.max(0, t));
  t = t * t * (3 - 2 * t);
  return k + t;
}

export function AssistantSection() {
  const { t, lang } = useTranslation();
  const a = t.landing.cover.assistant;
  const N = a.steps.length;

  const [active, setActive] = useState(0);

  const spacerRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const fillRef = useRef<HTMLDivElement | null>(null);
  const nodesRef = useRef<(HTMLDivElement | null)[]>([]);
  const counterRef = useRef<HTMLSpanElement | null>(null);
  const lastActive = useRef(-1);
  const lastPos = useRef(-1);

  useEffect(() => {
    let rafId = 0;
    let pending = false;

    const update = () => {
      const spacer = spacerRef.current;
      const track = trackRef.current;
      const fill = fillRef.current;
      if (!spacer || !track || !fill) return;

      const rect = spacer.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
      const pos = holdMap(progress * (N - 1), N);

      if (Math.abs(pos - lastPos.current) < 0.0005) return;
      lastPos.current = pos;

      track.style.transform = `translate3d(${-pos * track.clientWidth}px,0,0)`;
      fill.style.width = `${(pos / (N - 1)) * (((N - 1) / N) * 100)}%`;

      const a2 = Math.round(pos);
      if (a2 !== lastActive.current) {
        lastActive.current = a2;
        setActive(a2);
        nodesRef.current.forEach((node, idx) => {
          if (!node) return;
          node.classList.toggle("on", idx === a2);
          node.classList.toggle("done", idx < a2);
        });
        if (counterRef.current) {
          counterRef.current.textContent = `${String(a2 + 1).padStart(2, "0")} / 0${N}`;
        }
      }
    };

    const onScroll = () => {
      if (pending) return;
      pending = true;
      rafId = requestAnimationFrame(() => {
        pending = false;
        update();
      });
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [N]);

  return (
    <section className="companion">
      <div className="intro">
        <div className="kicker">{a.kicker}</div>
        <h2 className="h2">
          {a.h2a} <em>{a.h2em}</em>
        </h2>
        <p className="lead">{a.description}</p>
        <div className="scrollcue">
          {a.scrollCue}
          <span className="arr">
            <span>›</span>
            <span>›</span>
            <span>›</span>
          </span>
        </div>
      </div>

      <div className="spacer" ref={spacerRef} style={{ height: `calc(${N} * 132vh)` }}>
        <div className="pin">
          <div className="pin-head">
            <div className="pin-head-top">
              <span className="kk">{a.demoLabel}</span>
              <span className="ct" ref={counterRef}>{`01 / 0${N}`}</span>
            </div>
            <div className="track">
              <div className="track-line" />
              <div className="track-fill" ref={fillRef} />
              {a.steps.map((step, n) => (
                <div
                  key={n}
                  className={n === 0 ? "node on" : "node"}
                  ref={(el) => {
                    nodesRef.current[n] = el;
                  }}
                >
                  <span className="node-dot">{n + 1}</span>
                  <span className="node-label">{step.node}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="stage">
            <div className="h-track" ref={trackRef}>
              {a.steps.map((step, n) => (
                <div className="panel" key={n}>
                  <div className="p-meta">{step.meta}</div>
                  <AssistantChatDemo step={step} active={active === n} lang={lang} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="closer">
        <p>
          {a.closerA}
          <span>{a.closerEm}</span>
        </p>
        <Link href="/product/ai-assistant" className="companion-more">
          {`${a.learnMore} →`}
        </Link>
      </div>

      <style jsx global>{`
        .companion .intro {
          position: relative;
          z-index: 1;
          padding: 64px 24px 40px;
        }
        .companion .kicker {
          font-family: var(--f-mono, "Geist Mono", monospace);
          font-size: 11px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--terracotta);
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 14px;
        }
        .companion .kicker::before {
          content: "//";
          opacity: 0.5;
        }
        .companion .h2 {
          font-family: var(--f-display, "Fraunces", serif);
          font-weight: 360;
          font-size: 34px;
          line-height: 1.04;
          letter-spacing: -0.022em;
          margin: 0 0 14px;
          font-variation-settings: "opsz" 120, "SOFT" 40;
        }
        .companion .h2 em {
          font-style: italic;
          color: var(--terracotta);
          font-variation-settings: "opsz" 120, "SOFT" 100;
        }
        .companion .lead {
          font-size: 15px;
          line-height: 1.5;
          color: var(--ink-soft);
          margin: 0 0 22px;
          text-wrap: pretty;
          max-width: 34ch;
        }
        .companion .companion-more {
          display: inline-flex;
          align-items: center;
          font-family: var(--f-mono);
          font-size: 11px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--terracotta);
          text-decoration: none;
          margin: 22px 0 0;
        }
        .companion .companion-more:hover {
          text-decoration: underline;
          text-underline-offset: 4px;
        }
        .companion .scrollcue {
          display: flex;
          align-items: center;
          gap: 9px;
          font-family: var(--f-mono);
          font-size: 10.5px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--ink-faint);
        }
        .companion .scrollcue .arr {
          display: inline-flex;
          gap: 2px;
          color: var(--terracotta);
        }
        .companion .scrollcue .arr span {
          animation: companionNudge 1.6s infinite ease-in-out;
        }
        .companion .scrollcue .arr span:nth-child(2) {
          animation-delay: 0.15s;
        }
        .companion .scrollcue .arr span:nth-child(3) {
          animation-delay: 0.3s;
        }
        @keyframes companionNudge {
          0%,
          100% {
            opacity: 0.3;
            transform: translateX(0);
          }
          50% {
            opacity: 1;
            transform: translateX(2px);
          }
        }
        .companion .spacer {
          position: relative;
          z-index: 1;
        }
        .companion .pin {
          position: sticky;
          top: 0;
          height: 100vh;
          height: 100dvh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .companion .pin-head {
          flex: none;
          /* Top padding clears the 72px sticky site header so the progress
             rail (Create / Reflect / Recap) isn't tucked under the menu. */
          padding: 88px 24px 16px;
        }
        .companion .pin-head-top {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          margin-bottom: 16px;
        }
        .companion .pin-head-top .kk {
          font-family: var(--f-mono);
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--terracotta);
        }
        .companion .pin-head-top .kk::before {
          content: "// ";
          opacity: 0.5;
        }
        .companion .pin-head-top .ct {
          font-family: var(--f-mono);
          font-size: 10px;
          letter-spacing: 0.06em;
          color: var(--ink-faint);
        }
        .companion .track {
          position: relative;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
        }
        .companion .track-line {
          position: absolute;
          left: 16.66%;
          right: 16.66%;
          top: 15px;
          height: 1.5px;
          background: repeating-linear-gradient(
            90deg,
            var(--rule) 0 3px,
            transparent 3px 7px
          );
        }
        .companion .track-fill {
          position: absolute;
          left: 16.66%;
          top: 15px;
          height: 1.5px;
          background: var(--terracotta);
          width: 0;
          transition: width 0.2s linear;
        }
        .companion .node {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .companion .node-dot {
          position: relative;
          width: 31px;
          height: 31px;
          border-radius: 50%;
          background: var(--paper-2);
          border: 1.5px solid var(--rule);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--f-mono);
          font-size: 11px;
          color: var(--ink-faint);
          transition: border-color 0.3s, color 0.3s, background 0.3s, transform 0.3s;
        }
        .companion .node.done .node-dot {
          border-color: var(--terracotta);
          color: var(--terracotta);
        }
        .companion .node.on .node-dot {
          border-color: var(--terracotta);
          background: var(--terracotta);
          color: var(--terracotta-soft);
          transform: scale(1.08);
          box-shadow: 0 4px 12px rgba(184, 83, 26, 0.28);
        }
        .companion .node-label {
          font-size: 10.5px;
          font-weight: 500;
          color: var(--ink-faint);
          transition: color 0.3s;
          white-space: nowrap;
        }
        .companion .node.on .node-label {
          color: var(--terracotta);
        }
        .companion .stage {
          flex: 1 1 auto;
          position: relative;
          overflow: hidden;
          min-height: 0;
        }
        .companion .h-track {
          display: flex;
          height: 100%;
          will-change: transform;
        }
        .companion .panel {
          flex: 0 0 100%;
          height: 100%;
          padding: 4px 22px 36px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .companion .p-meta {
          font-family: var(--f-mono);
          font-size: 10px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--terracotta);
          margin-bottom: 14px;
        }
        .companion .cc {
          border: 1px solid var(--hair);
          border-radius: 18px;
          background: var(--paper);
          box-shadow: 0 1px 2px rgba(26, 24, 21, 0.04),
            0 18px 40px rgba(26, 24, 21, 0.08);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          cursor: pointer;
        }
        .companion .cc-top {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 13px 15px;
          border-bottom: 1px solid var(--hair);
          background: var(--paper-2);
        }
        .companion .cc-dot {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 1.5px solid var(--ink);
          position: relative;
          flex: none;
        }
        .companion .cc-dot::after {
          content: "";
          position: absolute;
          inset: 3.5px;
          border-radius: 50%;
          background: var(--terracotta);
        }
        .companion .cc-name {
          font-family: var(--f-display);
          font-style: italic;
          font-size: 15px;
          font-variation-settings: "opsz" 60, "SOFT" 60;
        }
        .companion .cc-live {
          margin-left: auto;
          font-family: var(--f-mono);
          font-size: 8.5px;
          letter-spacing: 0.1em;
          color: var(--moss);
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .companion .cc-live .lv {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--moss);
          box-shadow: 0 0 0 3px rgba(42, 74, 59, 0.13);
        }
        .companion .cc-ctx {
          font-family: var(--f-mono);
          font-size: 8.5px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--ink-faint);
          padding: 8px 15px;
          border-bottom: 1px solid var(--hair);
          background: var(--paper-2);
          display: flex;
          align-items: center;
          gap: 7px;
        }
        .companion .cc-ctx .pt {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--terracotta);
          flex: none;
        }
        .companion .cc-body {
          padding: 16px 15px 18px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .companion .bub {
          max-width: 86%;
          padding: 11px 13px;
          border-radius: 15px;
          font-size: 13px;
          line-height: 1.45;
        }
        .companion .bub.you {
          align-self: flex-end;
          background: var(--terracotta);
          color: var(--terracotta-soft);
          border-bottom-right-radius: 5px;
          font-family: var(--f-mono);
          font-size: 11.5px;
          letter-spacing: 0.01em;
          line-height: 1.4;
        }
        .companion .bub.you .sl {
          opacity: 0.7;
        }
        .companion .resp {
          display: grid;
          min-height: 74px;
        }
        .companion .resp > * {
          grid-area: 1/1;
          align-self: start;
        }
        .companion .typing {
          align-self: start;
          display: inline-flex;
          gap: 4px;
          padding: 13px 14px;
          background: var(--paper-2);
          border: 1px solid var(--hair);
          border-radius: 15px;
          border-bottom-left-radius: 5px;
          width: max-content;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .companion .typing.show {
          opacity: 1;
        }
        .companion .typing i {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--ink-faint);
          animation: companionBlink 1.2s infinite;
        }
        .companion .typing i:nth-child(2) {
          animation-delay: 0.2s;
        }
        .companion .typing i:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes companionBlink {
          0%,
          60%,
          100% {
            opacity: 0.3;
            transform: translateY(0);
          }
          30% {
            opacity: 1;
            transform: translateY(-3px);
          }
        }
        .companion .bub.ai {
          align-self: flex-start;
          background: var(--paper);
          border: 1px solid var(--hair);
          color: var(--ink);
          border-bottom-left-radius: 5px;
          box-shadow: 0 6px 18px rgba(26, 24, 21, 0.05);
          opacity: 0;
          transform: translateY(8px);
          transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.2, 0.7, 0.3, 1);
        }
        .companion .bub.ai.show {
          opacity: 1;
          transform: none;
        }
        .companion .bub.ai .tag {
          font-family: var(--f-mono);
          font-size: 8px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--terracotta);
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 6px;
        }
        .companion .bub.ai .tag .ck {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--terra-soft);
          position: relative;
          flex: none;
        }
        .companion .bub.ai .tag .ck::after {
          content: "";
          position: absolute;
          left: 4px;
          top: 2.5px;
          width: 3px;
          height: 5px;
          border: solid var(--terracotta);
          border-width: 0 1.4px 1.4px 0;
          transform: rotate(40deg);
        }
        .companion .closer {
          position: relative;
          z-index: 1;
          padding: 48px 24px 72px;
        }
        .companion .closer p {
          font-family: var(--f-display);
          font-style: italic;
          font-size: 22px;
          line-height: 1.25;
          color: var(--ink);
          margin: 0;
          font-variation-settings: "opsz" 72, "SOFT" 70;
          max-width: 18ch;
        }
        .companion .closer p span {
          color: var(--terracotta);
        }
        @media (prefers-reduced-motion: reduce) {
          .companion .scrollcue .arr span {
            animation: none;
          }
          .companion .bub.ai {
            transition: none;
          }
        }

        @media (min-width: 1024px) {
          .companion .intro {
            text-align: center;
            max-width: 940px;
            margin: 0 auto;
            padding: 88px 56px 8px;
          }
          .companion .kicker {
            justify-content: center;
          }
          .companion .scrollcue {
            justify-content: center;
          }
          .companion .h2 {
            font-size: 54px;
          }
          .companion .lead {
            margin: 0 auto 22px;
            max-width: 60ch;
            font-size: 19px;
          }
          .companion .companion-more {
            margin: 20px auto 0;
            font-size: 12px;
          }
          .companion .pin-head {
            padding: 96px 56px 18px;
            max-width: 1100px;
            margin: 0 auto;
            width: 100%;
          }
          .companion .track {
            max-width: 680px;
            margin: 0 auto;
          }
          .companion .node-dot {
            width: 36px;
            height: 36px;
            font-size: 12px;
          }
          .companion .track-line,
          .companion .track-fill {
            top: 17px;
          }
          .companion .panel {
            padding: 8px 24px 48px;
            align-items: center;
          }
          .companion .panel .cc {
            max-width: 520px;
            width: 100%;
          }
          .companion .panel .p-meta {
            text-align: center;
          }
          .companion .cc-name {
            font-size: 16px;
          }
          .companion .bub {
            font-size: 14px;
          }
          .companion .bub.you {
            font-size: 12.5px;
          }
          .companion .closer {
            text-align: center;
            padding: 64px 24px 96px;
          }
          .companion .closer p {
            margin: 0 auto;
            max-width: 24ch;
            font-size: 30px;
          }
        }
      `}</style>
    </section>
  );
}
